import { computed } from 'vue'
import { config } from '@/core/config'
import { i18n } from '@/core/i18n'
import { pinia } from '@/core/pinia'
import { useAuthStore } from '@/stores/auth/store'
import { useCtaStore } from '@/stores/cta/store'
import { getEKPUrl, isProduction, isMyEasybellCom } from '@/utils/environment'
import type { IconName } from '@easybell/caramel-vue3'

const ekpUrl = getEKPUrl()

declare type Locale = 'en' | 'de' | 'fr' | 'nl' | 'debug'

const locale = computed(() => {
  return i18n.global.locale.value
})

const isEasybellCom = computed(() => {
  return isMyEasybellCom()
})

export const languageMenuData = computed(() => {
  return {
    locale: locale.value as Locale,
    availableLocales: [
      {
        label: i18n.global.t('global.common.language.german'),
        value: 'de' as const,
      },
      {
        label: i18n.global.t('global.common.language.english'),
        value: 'en' as const,
      },
      {
        label: i18n.global.t('global.common.language.french'),
        value: 'fr' as const,
      },
      {
        label: i18n.global.t('global.common.language.dutch'),
        value: 'nl' as const,
      },
      ...(!isProduction()
        ? [
            {
              label: 'DEBUG',
              value: 'debug' as const,
            },
          ]
        : []),
    ],
  }
}) as any // TODO Design shared library doesn't support nl - for build I need to set this to any

export const myProductLinks = computed(() => {
  return isEasybellCom.value
    ? [
        {
          label: computed(() => i18n.global.t('menu.myProduct.label')).value,
          children: [
            {
              label: computed(() =>
                i18n.global.t('menu.subscription_management.label')
              ).value,
              link: `${ekpUrl}/subscription-management`,
              icon: '' as IconName,
            },
            {
              label: computed(() => i18n.global.t('menu.locations.label'))
                .value,
              link: `${ekpUrl}/shop/account-settings/locations`,
              icon: '' as IconName,
            },
          ],
        },
      ]
    : []
})

export const myDataLinks = computed(() => {
  return [
    {
      label: computed(() => i18n.global.t('menu.myData.label')).value,
      children: !isEasybellCom.value
        ? [
            {
              label: computed(() => i18n.global.t('menu.myProduct.label'))
                .value,
              link: `${ekpUrl}/produkt`,
              icon: '' as IconName,
            },
            {
              label: computed(() =>
                i18n.global.t('menu.personalInformation.label')
              ).value,
              link: `${ekpUrl}/kundendaten`,
              icon: '' as IconName,
            },
            {
              label: computed(() => i18n.global.t('menu.bills.label')).value,
              link: `${ekpUrl}/bills`,
              icon: '' as IconName,
            },
            {
              label: computed(() => i18n.global.t('menu.loginData.label'))
                .value,
              link: `${ekpUrl}/account-settings`,
              icon: '' as IconName,
            },
            {
              label: computed(() => i18n.global.t('menu.switchPlan.label'))
                .value,
              link: `${ekpUrl}/products/edit`,
              icon: '' as IconName,
            },
            {
              label: computed(() => i18n.global.t('menu.notifications.label'))
                .value,
              link: `${ekpUrl}/notifications`,
              icon: '' as IconName,
            },
          ]
        : [
            {
              label: computed(() => i18n.global.t('menu.bills.label')).value,
              link: `${ekpUrl}/bills`,
              icon: '' as IconName,
            },
            ...(!useAuthStore(pinia).isOfficeDesk ||
            useAuthStore(pinia).getHasOfficeDeskPermission('callLogPermission')
              ? [
                  {
                    label: computed(() =>
                      i18n.global.t('menu.callHistory.label')
                    ).value,
                    link: `${ekpUrl}/call-history`,
                    icon: '' as IconName,
                  },
                ]
              : []),
          ],
    },
  ]
})

export const phoneSettingsLinks = computed(() => {
  return [
    {
      label: computed(() => i18n.global.t('menu.phoneSettings.label')).value,
      children: [
        ...(!useAuthStore(pinia).isOfficeDesk
          ? [
              {
                label: computed(() => i18n.global.t('menu.manageNumbers.label'))
                  .value,
                link: !isEasybellCom.value
                  ? `${ekpUrl}/phonesettings`
                  : `${ekpUrl}/phone-settings`,
                icon: '' as IconName,
              },
            ]
          : []),
        {
          label: computed(() => i18n.global.t('menu.cta.label')).value,
          link: '/',
          icon: '' as IconName,
        },
        ...(!useAuthStore(pinia).isOfficeDesk
          ? [
              {
                label: computed(() => i18n.global.t('menu.ttc.label')).value,
                link: !useCtaStore(pinia).getHasTcProduct
                  ? `${ekpUrl}/order/ttc`
                  : `${config.TTC_URL}`,
                icon: '' as IconName,
              },
            ]
          : []),
        ...(!useAuthStore(pinia).isOfficeDesk ||
        useAuthStore(pinia).getHasOfficeDeskPermission('faxPermission')
          ? [
              {
                label: computed(() => i18n.global.t('menu.fax.label')).value,
                link: `${ekpUrl}/fax`,
                icon: '' as IconName,
              },
            ]
          : []),
        ...((!useAuthStore(pinia).isOfficeDesk ||
          useAuthStore(pinia).getHasOfficeDeskPermission('smsPermission')) &&
        useCtaStore(pinia).getSmsServiceAvailable &&
        !isEasybellCom.value
          ? [
              {
                label: computed(() => i18n.global.t('menu.sms.label')).value,
                link: `${ekpUrl}/sms`,
                icon: '' as IconName,
              },
            ]
          : []),
        ...(!useAuthStore(pinia).isOfficeDesk ||
        (useAuthStore(pinia).getHasOfficeDeskPermission('callLogPermission') &&
          !isEasybellCom.value)
          ? [
              {
                label: computed(() => i18n.global.t('menu.callHistory.label'))
                  .value,
                link: `${ekpUrl}/call-history`,
                icon: '' as IconName,
              },
            ]
          : []),
      ],
    },
  ]
})

export const shopLinks = computed(() => {
  return !isEasybellCom.value
    ? [
        {
          label: computed(() => i18n.global.t('menu.shop.label')).value,
          children: [
            {
              label: computed(() => i18n.global.t('menu.products.label')).value,
              link: `${ekpUrl}/shop/products`,
              icon: '' as IconName,
            },
            {
              label: computed(() => i18n.global.t('menu.cart.label')).value,
              link: `${ekpUrl}/shop/cart`,
              icon: '' as IconName,
            },
          ],
        },
      ]
    : []
})

export const helpLinks = computed(() => {
  return [
    {
      label: computed(() => i18n.global.t('menu.help.label')).value,
      children: [
        {
          label: computed(() => i18n.global.t('menu.helpContact.label')).value,
          link: computed(() => i18n.global.t('menu.helpContact.link')).value,
          icon: '' as IconName,
        },
        {
          label: computed(() => i18n.global.t('menu.helpCenter.label')).value,
          link: computed(() => i18n.global.t('menu.helpCenter.link')).value,
          icon: '' as IconName,
        },
        ...(!isEasybellCom.value
          ? [
              {
                label: computed(() => i18n.global.t('menu.speedcheck.label'))
                  .value,
                link: `${ekpUrl}/speedcheck`,
                icon: '' as IconName,
              },
            ]
          : []),
      ],
    },
  ]
})

export const userMenuItems = computed(() => {
  return [
    {
      label: computed(() => i18n.global.t('global.logout')).value,
      link: '/logout',
      icon: 'Exit' as IconName,
      hasDividerBefore: true,
    },
  ]
})
